import { Injectable } from '@angular/core';
import { Geolocation, Position } from '@capacitor/geolocation';

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  private static readonly TIMEOUT_MS = 10_000;
  private static readonly MAXIMUM_AGE_MS = 60_000;
  private static readonly SEND_POINT_TIMEOUTS = [20_000, 45_000];

  /**
   * Obtiene la posición actual con un timeout garantizado.
   *
   * El plugin de Capacitor en iOS no aplica un timeout por defecto (a diferencia
   * de Android y Web), por lo que `Geolocation.getCurrentPosition()` puede quedar
   * pendiente para siempre si el GPS no consigue un fix. `Promise.race` garantiza
   * que esta promesa siempre se rechace en el tiempo límite.
   *
   * @throws Error con mensaje 'Geolocation timeout' si se agota el tiempo límite,
   *         o el error original del plugin si `getCurrentPosition` rechaza.
   */
  async getCurrentPositionWithTimeout(options?: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
  }): Promise<Position> {
    const timeout = options?.timeout ?? GeolocationService.TIMEOUT_MS;
    const maximumAge = options?.maximumAge ?? GeolocationService.MAXIMUM_AGE_MS;
    const enableHighAccuracy = options?.enableHighAccuracy ?? true;

    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(
        () => reject(new Error('Geolocation timeout')),
        timeout + 5_000
      );
    });

    try {
      return await Promise.race<Position>([
        Geolocation.getCurrentPosition({
          enableHighAccuracy,
          timeout,
          maximumAge,
        }),
        timeoutPromise,
      ]);
    } finally {
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
      }
    }
  }

  /**
   * Obtiene la posición del punto de cierre (send_point) con reintentos.
   * Prioriza la precisión: enableHighAccuracy y maximumAge 0 (fix fresco).
   *
   * @param onAttempt callback opcional invocado al inicio de cada intento.
   * @throws El último error del plugin si todos los intentos fallan.
   */
  async getSendPointPosition(
    onAttempt?: (attempt: number, maxAttempts: number) => void
  ): Promise<Position> {
    const timeouts = GeolocationService.SEND_POINT_TIMEOUTS;
    let lastError: unknown;

    for (let attempt = 1; attempt <= timeouts.length; attempt++) {
      onAttempt?.(attempt, timeouts.length);
      try {
        return await this.getCurrentPositionWithTimeout({
          enableHighAccuracy: true,
          timeout: timeouts[attempt - 1],
          maximumAge: 0,
        });
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  }

  /**
   * Determina si un error de geolocalización corresponde a un permiso denegado.
   * Útil para diferenciar mensajes de usuario entre permiso denegado y timeout.
   */
  isPermissionDeniedError(error: unknown): boolean {
    const anyError = error as { message?: string; code?: number } | undefined;
    if (!anyError) return false;
    if (anyError.code === 1) return true;
    const message = (anyError.message ?? '').toLowerCase();
    return (
      message.includes('denied') ||
      message.includes('denegad') ||
      message.includes('permission') ||
      message.includes('permiso')
    );
  }

  /**
   * Determina si un error de geolocalización corresponde a un timeout de GPS
   * (el plugin no consiguió un fix dentro del tiempo límite).
   * Estos errores son esperados (interiores) y NO deben reportarse a Sentry.
   */
  isTimeoutError(error: unknown): boolean {
    const message = ((error as { message?: string })?.message ?? '').toLowerCase();
    return (
      message.includes('could not obtain location in time') ||
      message.includes('geolocation timeout')
    );
  }
}
